import type { Project } from '@/models/Project'
import { Progression, type Widget } from '@/models/Widget'

/**
 * The dashboard's portfolio: each QMS with the technical files attached to it
 * (`parent_qms_project`), then the products no QMS of the company carries, then
 * the regulatory watches. Pure functions, so the grouping, the search and the
 * "worth a look" rules are unit-tested without mounting anything.
 */

/** What the dashboard knows of one project's score, whichever engine produced it. */
/** Where one project's score read stands. « Not assessed », « nothing to report »
 * are said only once a read has SUCCEEDED (client-data-contract §4): before, the
 * dashboard waits; after a failure, it says it could not read. */
export type ScoreRead = 'pending' | 'ok' | 'failed'

export interface ProjectScore {
  /** The widget the gauges render, or null when the project has never been assessed. */
  widget: Widget | null
  /** Verdicts resting on documents changed since (engine projects only). */
  staleVerdicts: number
  read: ScoreRead
}

/** The dashboard's reads as a whole: pending while any is still being read, failed when any failed. */
export function scoresRead(scores: ReadonlyArray<ProjectScore | undefined>): ScoreRead {
  if (scores.some((s) => !s || s.read === 'pending')) return 'pending'
  return scores.some((s) => s?.read === 'failed') ? 'failed' : 'ok'
}

/**
 * What the portfolio holds, for the opening: nothing at all, regulatory watches
 * only (no score will ever come), or QMS / products whose scores it reads.
 */
export type PortfolioKind = 'none' | 'watch_only' | 'scored'

export function portfolioKind(projects: readonly Project[]): PortfolioKind {
  const p = buildPortfolio(projects)
  if (portfolioSize(p) === 0) return 'none'
  return p.groups.length === 0 ? 'watch_only' : 'scored'
}

/**
 * What the dashboard's opening says, as locale keys under `dashboard.` (null headline: the greeting alone).
 * MYAC-1522 (onboarding pass) — with nothing to show, or a watch only, it says that,
 * not « your files have not been assessed yet ».
 */
export function openingKeys(
  read: ScoreRead, flags: number, anyScore: boolean, kind: PortfolioKind = 'scored', isAdmin = true,
): { headline: string | null; prose: string | null } {
  if (kind === 'none') return { headline: 'headline_none', prose: isAdmin ? 'prose_none_admin' : 'prose_none_member' }
  if (kind === 'watch_only') return { headline: 'headline_calm', prose: 'prose_watch_only' }
  if (read === 'pending') return { headline: null, prose: null }
  if (read === 'failed') return { headline: flags ? 'headline_flags' : 'headline_unread', prose: 'prose_unread' }
  if (flags) return { headline: 'headline_flags', prose: 'prose_flags' }
  // Nothing assessed yet: not « nothing needs your attention » but where to go on
  // (MYAC-1522 lot 3, decision L).
  return anyScore ? { headline: 'headline_calm', prose: 'prose_calm' } : { headline: 'headline_first', prose: 'prose_empty_scores' }
}

export interface QmsGroup {
  /** The QMS heading the group; null for the products no QMS of the company carries. */
  qms: Project | null
  /** False when a search keeps only some of its products: the QMS then only names the group. */
  qmsVisible: boolean
  tds: Project[]
}

export interface Portfolio {
  groups: QmsGroup[]
  watches: Project[]
}

/** How many projects a (filtered) portfolio shows. */
export function portfolioSize(p: Portfolio): number {
  return p.groups.reduce((n, g) => n + (g.qmsVisible ? 1 : 0) + g.tds.length, 0) + p.watches.length
}

export type PortfolioFilter = 'all' | 'qms' | 'td' | 'watch'

const byName = (a: Project, b: Project) =>
  (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' })

export function buildPortfolio(projects: readonly Project[]): Portfolio {
  const qmsList = projects.filter((p) => p.type === 'qms').sort(byName)
  const tds = projects.filter((p) => p.type === 'td').sort(byName)
  const watches = projects.filter((p) => p.type === 'regulatory_monitoring').sort(byName)

  const byQms = new Map<string, Project[]>(qmsList.map((q) => [q.id, []]))
  const orphans: Project[] = []
  for (const td of tds) {
    const parent = td.parent_qms_project?.id
    const list = parent ? byQms.get(parent) : undefined
    if (list) list.push(td)
    else orphans.push(td)
  }

  const groups: QmsGroup[] = qmsList.map((qms) => ({ qms, qmsVisible: true, tds: byQms.get(qms.id) ?? [] }))
  if (orphans.length) groups.push({ qms: null, qmsVisible: false, tds: orphans })
  return { groups, watches }
}

/** Case- and accent-insensitive, so « therm » finds « Thermomètre ». */
export function normalizeForSearch(text: string | null | undefined): string {
  return (text ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
}

export function matchesQuery(query: string, ...fields: Array<string | null | undefined>): boolean {
  const q = normalizeForSearch(query.trim())
  return !q || fields.some((f) => normalizeForSearch(f).includes(q))
}

export interface HighlightParts {
  before: string
  match: string
  after: string
}

/** Split `text` around the first occurrence of `query` (accents ignored), for highlighting. */
export function highlightParts(text: string, query: string): HighlightParts {
  const q = query.trim()
  if (!q) return { before: text, match: '', after: '' }
  // NFD can change the length of the text: only highlight when it does not.
  const haystack = normalizeForSearch(text)
  const at = haystack.length === text.length ? haystack.indexOf(normalizeForSearch(q)) : -1
  if (at < 0) return { before: text, match: '', after: '' }
  return { before: text.slice(0, at), match: text.slice(at, at + q.length), after: text.slice(at + q.length) }
}

const regulationLabel = (p: Project) =>
  p.td_regulation === 'mdr' ? 'MDR' : p.td_regulation === 'ivdr' ? 'IVDR' : ''

/** Every text a project can be found by. */
export function searchFields(p: Project): string[] {
  return [p.name, p.product_name, p.project_company_name, regulationLabel(p)].filter(
    (f): f is string => !!f,
  )
}

/**
 * The portfolio as it shows for a query and a filter. A query matching a QMS
 * keeps the whole group (its products included): searching a company's QMS
 * means wanting what hangs under it.
 */
export function filterPortfolio(
  portfolio: Portfolio,
  query: string,
  filter: PortfolioFilter,
  keep: (p: Project) => boolean = () => true,
): Portfolio {
  const showQms = filter === 'all' || filter === 'qms'
  const showTds = filter === 'all' || filter === 'td'
  const showWatches = filter === 'all' || filter === 'watch'

  const groups: QmsGroup[] = []
  for (const group of portfolio.groups) {
    const qmsHit = !!group.qms && matchesQuery(query, ...searchFields(group.qms))
    const tds = showTds
      ? group.tds.filter((td) => (qmsHit || matchesQuery(query, ...searchFields(td))) && keep(td))
      : []
    const qmsVisible = showQms && qmsHit && !!group.qms && keep(group.qms)
    if (qmsVisible || tds.length) groups.push({ qms: group.qms, qmsVisible, tds })
  }
  const watches = showWatches ? portfolio.watches.filter((w) => matchesQuery(query, ...searchFields(w)) && keep(w)) : []
  return { groups, watches }
}

/** Why a project deserves a look, in order of weight — or null when it does not. */
export type AttentionReason = 'stale' | 'decreasing'

export function attentionReason(score: ProjectScore | undefined): AttentionReason | null {
  if (!score?.widget) return null
  if (score.staleVerdicts > 0) return 'stale'
  if (score.widget.mac_score_data?.progression === Progression.DECREASING) return 'decreasing'
  return null
}

/** The A→F distribution as percentages of the rated requirements (all zero when none). */
export function distributionPercents(widget: Widget | null): Record<'a' | 'b' | 'c' | 'd' | 'f', number> {
  const dist = widget?.mac_score_data?.score_distribution
  const total = dist ? dist.a + dist.b + dist.c + dist.d + dist.f : 0
  const pct = (n: number) => (total ? (n / total) * 100 : 0)
  return { a: pct(dist?.a ?? 0), b: pct(dist?.b ?? 0), c: pct(dist?.c ?? 0), d: pct(dist?.d ?? 0), f: pct(dist?.f ?? 0) }
}

/** Where a score is heading — what the dashboard's « pulse » counts and filters on. */
export type TrendCategory = 'up' | 'flat' | 'down' | 'none'

/** null while the score is unread (pending or failed): it is in no counter, not « not assessed ». */
export function trendCategory(score: ProjectScore | undefined): TrendCategory | null {
  if (score?.read !== 'ok') return null
  const data = score.widget?.mac_score_data
  if (!data) return 'none'
  if (data.progression === Progression.INCREASING) return 'up'
  if (data.progression === Progression.DECREASING) return 'down'
  return 'flat'
}
